import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import {
  AcademicSession,
  AcademicSessionCreateInput,
  Classroom,
  ClassroomCreateInput,
  Organization,
  OrganizationCreateInput,
  RosteringGrade,
} from './organization.model';
import { ApiGatewayService } from '@wam/authentication';
import { map, mergeMap } from 'rxjs/operators';
import { isEmpty, omitBy } from 'lodash-es';
import { DateTransformer } from '@app/shared/date-transformer.data';
import { WithAuthService } from '@app/shared/services/with-auth.service';

@Injectable({
  providedIn: 'root',
})
export class OrganizationsDataService extends WithAuthService {
  private apiGatewayService = inject(ApiGatewayService);

  getOrganization(customerId: string, organizationId: string): Observable<Organization> {
    return this.apiGatewayService
      .simpleGet<{
        org: Organization;
      }>(`rostering/v2/rostering/source/${customerId}/orgs/${organizationId}`)
      .pipe(map(({ org }) => org));
  }

  getOrganizations(): Observable<Organization[]> {
    return this.withUser().pipe(
      mergeMap(({ organization }) =>
        this.apiGatewayService.simpleGet<{ orgs: Organization[] }>(
          `rostering/v2/rostering/source/${organization}/orgs?limit=1000&offset=0&sort=name`,
        ),
      ),
      map(({ orgs }) => orgs),
    );
  }

  getClass(classId: string, schoolId: string, organizationId: string): Observable<Classroom> {
    return this.apiGatewayService
      .simpleGet<{
        class: Classroom;
      }>(`rostering/v2/rostering/source/${organizationId}/orgs/${schoolId}/classes/${classId}`)
      .pipe(map((response) => response.class));
  }

  getClasses(schoolId: string): Observable<Classroom[]> {
    return this.withUser().pipe(
      mergeMap(({ organization }) =>
        this.apiGatewayService.simpleGet<{ classes: Classroom[] }>(
          `rostering/v2/rostering/source/${organization}/orgs/${schoolId}/classes?sort=title`,
        ),
      ),
      map(({ classes }) => classes),
    );
  }

  /**
   * Some roles (e.g. `ext-parent`) authenticate with a token that has no `organization` claim
   * at all - see `AuthenticationService.buildUser()`. Additionally, this can be called before
   * the current user has been resolved in the store at all (e.g. during early bootstrap), in
   * which case `withUser()` emits `null`. In both cases there's no organization to look up an
   * academic session for, so short-circuit to `undefined` instead of issuing a request with
   * `undefined`/`null` baked into the URL (which the backend rejects) or throwing while trying
   * to destructure a `null` user. This is a no-op for the normal case where a user with an
   * organization is already available.
   */
  getAcademicSession(): Observable<AcademicSession> {
    return this.withUser().pipe(
      mergeMap((user) => {
        const organization = user?.organization;

        return organization
          ? this.apiGatewayService
              .simpleGet<{ academicSessions: AcademicSession[] }>(
                `rostering/v2/rostering/source/${organization}/orgs/${organization}/academicSessions`,
              )
              .pipe(map(({ academicSessions }) => academicSessions?.[0]))
          : of(undefined);
      }),
      map((academicSession: AcademicSession) => {
        if (academicSession) {
          academicSession.sDate = DateTransformer.transform(academicSession.startDate);
          academicSession.eDate = DateTransformer.transform(academicSession.endDate);
        }
        return academicSession;
      }),
    );
  }

  getLastHistoricalAcademicSession(): Observable<AcademicSession> {
    return this.withUser().pipe(
      mergeMap(({ organization }) =>
        this.apiGatewayService.simpleGet<{ academicSessions: AcademicSession[] }>(
          `rostering/v2/rostering/source/${organization}/orgs/${organization}/academicSessions?filter=type='historicalSchoolYear'`,
        ),
      ),
      map(({ academicSessions }) =>
        [...(academicSessions ?? [])]
          .sort((sessionA, sessionB) => {
            const endDateA = new Date(sessionA.endDate).getTime();
            const endDateB = new Date(sessionB.endDate).getTime();
            return endDateB - endDateA;
          })
          .at(0),
      ),
      map((academicSession: AcademicSession) => {
        if (academicSession) {
          academicSession.sDate = DateTransformer.transform(academicSession.startDate);
          academicSession.eDate = DateTransformer.transform(academicSession.endDate);
        }
        return academicSession;
      }),
    );
  }

  createOrganization(organizationCreateInput: OrganizationCreateInput): Observable<Organization> {
    return this.withUser().pipe(
      mergeMap(({ organization }) =>
        this.apiGatewayService.simplePost<{ org: Organization }>(
          `rostering/v2/rostering/source/${organization}/orgs`,
          omitBy(organizationCreateInput, isEmpty),
        ),
      ),
      map(({ org }) => org),
    );
  }

  updateOrganization(
    sourceId: string,
    organizationUpdateInput: OrganizationCreateInput,
  ): Observable<Organization> {
    return this.withUser().pipe(
      mergeMap(({ organization }) =>
        this.apiGatewayService.simplePut<{ org: Organization }>(
          `rostering/v2/rostering/source/${organization}/orgs/${sourceId}`,
          omitBy(organizationUpdateInput, isEmpty),
        ),
      ),
      map(({ org }) => org),
    );
  }

  deleteOrganization(sourceId: string): Observable<void> {
    return this.withUser().pipe(
      mergeMap(({ organization }) =>
        this.apiGatewayService.delete<void>(
          `rostering/v2/rostering/source/${organization}/orgs/${sourceId}`,
        ),
      ),
    );
  }

  createAcademicSession(
    sessionCreateInput: AcademicSessionCreateInput,
  ): Observable<AcademicSession> {
    return this.withUser().pipe(
      mergeMap(({ organization }) =>
        this.apiGatewayService.simplePost<{ academicSession: AcademicSession }>(
          `rostering/v2/rostering/source/${organization}/orgs/${organization}/academicSessions`,
          sessionCreateInput,
        ),
      ),
      map(({ academicSession }) => academicSession),
    );
  }

  updateAcademicSession(
    sourceId: string,
    sessionUpdateInput: AcademicSessionCreateInput,
  ): Observable<AcademicSession> {
    return this.withUser().pipe(
      mergeMap(({ organization }) =>
        this.apiGatewayService.simplePut<{ academicSession: AcademicSession }>(
          `rostering/v2/rostering/source/${organization}/orgs/${organization}/academicSessions/${sourceId}`,
          sessionUpdateInput,
        ),
      ),
      map(({ academicSession }) => academicSession),
    );
  }

  createClassroom(
    schoolSourcedId: string,
    classroomCreateInput: ClassroomCreateInput,
  ): Observable<Classroom> {
    return this.withUser().pipe(
      mergeMap(({ organization }) =>
        this.apiGatewayService.simplePost<{ class: Classroom }>(
          `rostering/v2/rostering/source/${organization}/orgs/${schoolSourcedId}/classes`,
          classroomCreateInput,
        ),
      ),
      map((response) => response.class),
    );
  }

  updateClassroom(
    sourcedId: string,
    schoolSourcedId: string,
    classroomUpdateInput: ClassroomCreateInput,
  ): Observable<Classroom> {
    return this.withUser().pipe(
      mergeMap(({ organization }) =>
        this.apiGatewayService.simplePut<{ class: Classroom }>(
          `rostering/v2/rostering/source/${organization}/orgs/${schoolSourcedId}/classes/${sourcedId}`,
          classroomUpdateInput,
        ),
      ),
      map((response) => response.class),
    );
  }

  deleteClassroom(sourcedId: string, schoolSourcedId: string): Observable<void> {
    return this.withUser().pipe(
      mergeMap(({ organization }) =>
        this.apiGatewayService.delete<void>(
          `rostering/v2/rostering/source/${organization}/orgs/${schoolSourcedId}/classes/${sourcedId}`,
        ),
      ),
    );
  }

  getGrades(): Observable<RosteringGrade[]> {
    return this.apiGatewayService
      .simpleGet<{ grades: RosteringGrade[] }>('rostering/v2/rostering/static/grades')
      .pipe(map(({ grades }) => grades));
  }

  getTimezones(): Observable<string[]> {
    return this.apiGatewayService
      .simpleGet<{
        timezones: { key: number; value: string }[];
      }>('rostering/v2/rostering/static/timezones')
      .pipe(map(({ timezones }) => timezones.map((timezone) => timezone.value)));
  }
}
